<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	// Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	// Helpers
	import {
		setupGen1EmeterTimerLogic,
		createGen1StatusLabels,
		createGen1CombinedLabelList,
		canShowGen1LabelList,
		getGen1DeviceStatuses,
	} from '../../gen1-main-helpers';
	import { getEmeterDataG1DisplayedFields } from 'src/helpers/emeters_data_helpers';
	import { convert } from 'src/helpers/utilities';

	// Design System Components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';

	// Components
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import CustomBoundary from 'src/components/ErrorBoundary/CustomBoundary.svelte';
	import EmeterDataG1V2 from 'src/components/EmeterData/EmeterDataG1V2.svelte';
	import PeakPowerInfoV2 from 'src/components/Consumption/PeakPowerInfoV2.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	// Navigation hook
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	// Types
	import type { Device } from 'src/types/Device.ts';

	/**
	 * Main control tab for the SHEM-3 (Gen1 emeter) sidepanel drawer.
	 *
	 * Responsibilities:
	 * - displays active Gen1 emeter timers and remaining time when available
	 * - renders emeter data (phase a, b, c) with power, voltage, current, and total energy
	 * - shows peak power information
	 * - displays charts when configured
	 * - shows firmware incompatibility warnings and provides the relay control
	 *
	 * emeterDataG1Fields - Get fields displayed by EmeterDataG1 to filter out duplicates
	 * filteredLabelList - Filter out duplicate fields from labelList (keep timerLabels as they are)
	 *
	 * @prop device Active device selected in the sidepanel drawer. Expected to be a Gen1 emeter.
	 */
	export let device: Device;

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceConfig = devices_config?.[device?.type];

	// Device calculations
	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	// Status and label processing
	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses, device);

	$: emeterDataG1Fields =
		status?.online && device ? getEmeterDataG1DisplayedFields(device, status) : [];

	$: filteredLabelList = Array.isArray(labelList)
		? labelList.filter((entry) => !emeterDataG1Fields.includes(entry.name))
		: [];

	$: combinedLabelList = createGen1CombinedLabelList(timerLabels, filteredLabelList);

	let timerLabels: Record<string, any> = {};
	let emeter_timer = false;

	// Total Energy for display (Gen1: emeters[channel].total)
	$: totalEnergyValue =
		status && device != null ? status?.emeters?.[device?.channel]?.total : undefined;
	$: totalEnergyDisplay =
		totalEnergyValue != null && !Number.isNaN(totalEnergyValue)
			? { value: convert(totalEnergyValue, 'Wh'), hasValue: true }
			: { value: 'N/A', hasValue: false };

	beforeUpdate(() => {
		if (
			device &&
			status !== undefined &&
			settings !== undefined &&
			deviceConfig !== undefined &&
			status?.online
		) {
			emeter_timer = setupGen1EmeterTimerLogic(device, status, false);
		}
	});
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if device && status !== undefined && settings !== undefined && deviceConfig !== undefined && status?.online}
			{#if emeter_timer}
				{#key status['relays'][0].timer_started}
					<TimerRemaining
						timerKey="gen1_emeter"
						{device}
						{status}
						{settings}
						bind:timerLabels
					/>
				{/key}
			{/if}
			{#if canShowGen1LabelList(device, status)}
				<DeviceLabelList labelList={combinedLabelList} />
			{/if}
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div class="flex-full-height justify-content-top" slot="content">
		<CustomBoundary
			onError={console.error}
			message={'Problem loading emeter data. Please contact App support.'}
		>
			{#if status?.emeters}
				<EmeterDataG1V2 {device} {status} />
			{/if}
		</CustomBoundary>
		<ParameterCardV2
			isFocusable
			title={totalEnergyDisplay.value}
			description={$format('text_total_energy', { default: 'Total Energy' })}
			size="s"
			class="mb-s"
			data-prevent-drawer-swipe
			data-prevent-sidepanel-swipe
			role={deviceChartsConfig.length ? 'button' : undefined}
			on:click={() => deviceChartsConfig.length && navigateToHistoryStatisticsTab()}
		/>
	</div>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<PeakPowerInfoV2 {device} />
		<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
	</div>
</DeviceViewStateWrapper>
